import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { useUser } from "@clerk/clerk-react";
import {
  AlertTriangle, Apple, ShowerHead, Shield, CheckCircle2, Clock, CalendarDays,
  Timer, HeartPulse, Scale, AlertCircle, Utensils, Dumbbell, Info, Sun, Moon,
  Calendar, Droplet, Pill, Target, ArrowRight, Activity, Brain, PlayCircle,
  Syringe, ClipboardCheck, Stethoscope, ShieldCheck, Crown, Lock
} from 'lucide-react';
import { useAnalysis } from '../AnalysisContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import PreventiveCareCard from './care/PreventiveCareCard';
import CondicionesCriticasCard from './care/CondicionesCriticasCard';
import EjercicioCard from './care/EjercicioCard';

// Helper Functions
const safeMap = (array = [], renderFn) => {
  if (!Array.isArray(array)) return null;
  return array.map(renderFn);
};

const hasData = (data) => {
  if (!data) return false;
  if (Array.isArray(data)) return data.length > 0;
  if (typeof data === 'object') return Object.keys(data).length > 0;
  return false;
};

// Utility component for loading state
const LoadingState = () => (
  <Card className="max-w-md mx-auto mt-8 bg-gradient-to-br from-orange-50 to-amber-50 shadow-lg border-none">
    <CardContent className="p-8">
      <div className="flex flex-col items-center justify-center space-y-4">
        <div className="relative">
          <div className="bg-orange-100 p-4 rounded-full">
            <HeartPulse className="h-8 w-8 text-orange-500" />
            <motion.div
              className="absolute -bottom-1 -right-1 h-3 w-3 bg-green-400 rounded-full"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </div>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="absolute"
        >
          <div className="h-16 w-16 rounded-full border-4 border-orange-200 border-t-orange-500" />
        </motion.div>
        <div className="text-center space-y-2 mt-4">
          <h3 className="text-lg font-semibold text-gray-800">
            Analizando el estado de salud
          </h3>
          <p className="text-sm text-gray-600">
            Procesando los indicadores de bienestar de tu mascota
          </p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const PremiumGate = () => (
  <div className="min-h-[80vh] flex items-center justify-center p-3 sm:hidden">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md bg-gradient-to-br from-purple-50 to-white rounded-xl shadow-lg p-4 border border-purple-100"
    >
      <div className="flex flex-col items-center text-center space-y-3">
        <div className="relative mb-1">
          <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full blur opacity-25"></div>
          <div className="relative bg-white p-3 rounded-full">
            <Crown className="h-8 w-8 text-purple-600" />
          </div>
        </div>
        
        <div>
          <h2 className="text-xl font-bold text-gray-800 mb-1">Contenido Premium</h2>
          <p className="text-sm text-gray-600">
            Accede a guías de cuidado detalladas para tu mascota
          </p>
        </div>
        
        <div className="w-full grid grid-cols-1 gap-2 my-2">
          {[
            'Plan preventivo personalizado',
            'Guías de cuidado diario',
            'Recomendaciones de ejercicio'
          ].map((feature, index) => (
            <div key={index} className="flex items-center space-x-2 bg-white p-2 rounded-lg shadow-sm">
              <CheckCircle2 className="h-4 w-4 text-purple-600 flex-shrink-0" />
              <span className="text-gray-700 text-sm">{feature}</span>
            </div>
          ))}
        </div>
        
        <button 
          onClick={() => window.location.href = '/pricing'}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2.5 px-4 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center space-x-2 text-sm"
        >
          <Lock className="h-4 w-4" />
          <span>Desbloquear Premium</span>
        </button>
      </div>
    </motion.div>
  </div>
);

const NoAnalysisGate = () => (
  <div className="min-h-[80vh] flex items-center justify-center p-3 sm:hidden">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md bg-gradient-to-br from-blue-50 to-white rounded-xl shadow-lg p-4 border border-blue-100"
    >
      <div className="flex flex-col items-center text-center space-y-3">
        <div className="relative mb-1">
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full blur opacity-25"></div>
          <div className="relative bg-white p-3 rounded-full">
            <Target className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        
        <div>
          <h2 className="text-xl font-bold text-gray-800 mb-1">Plan de Cuidados</h2>
          <p className="text-sm text-gray-600">
            Realiza un análisis integral para obtener tu plan de cuidados personalizado
          </p>
        </div>
        
        <div className="w-full grid grid-cols-1 gap-2 my-2">
          {[
            'Plan preventivo a medida',
            'Rutinas de ejercicio',
            'Guía de cuidados diarios'
          ].map((feature, index) => (
            <div key={index} className="flex items-center space-x-2 bg-white p-2 rounded-lg shadow-sm">
              <CheckCircle2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
              <span className="text-gray-700 text-sm">{feature}</span>
            </div>
          ))}
        </div>
        
        <button 
          onClick={() => window.location.href = '/analysis'}
          className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-2.5 px-4 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center space-x-2 hover:from-blue-600 hover:to-cyan-600 text-sm"
        >
          <Target className="h-4 w-4" />
          <span>Realizar Análisis Integral</span>
        </button>
      </div>
    </motion.div>
  </div>
);

// Main CareTab Component
const CareTab = () => {
  const { analysisData, breedGuide } = useAnalysis();
  const { user } = useUser();
  const isPremium = user?.unsafeMetadata?.isPremium;

  if (!isPremium) {
    return <PremiumGate />;
  }

  if (!breedGuide?.cuidadosSalud) {
    return <NoAnalysisGate />;
  }

  if (!analysisData) {
    return <LoadingState />;
  }

  return (
    <div className="max-w-6xl mx-auto p-2 sm:p-4 space-y-4 sm:space-y-6">
      {/* Context Notice */}
      <Alert>
        <Info className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        <AlertDescription className="text-sm sm:text-sm">
          Este plan de cuidados está personalizado según la información proporcionada.
          Ajusta las rutinas según las necesidades específicas de tu mascota.
        </AlertDescription>
      </Alert>

      <PreventiveCareCard planPreventivo={breedGuide?.cuidadosSalud?.planPreventivo} />
      <CondicionesCriticasCard condicionesCriticas={breedGuide?.cuidadosSalud?.condicionesCriticas} />
     <EjercicioCard ejercicioYDiversion={breedGuide?.cuidadosSalud?.ejercicioYDiversion} />

      {/* Important Notes */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-500" />
        <AlertDescription className="space-y-2 text-sm sm:text-sm text-blue-700">
          <p>
            Este plan de cuidados está diseñado como una guía general. Ajusta los horarios y rutinas según:
          </p>
          <ul className="list-disc pl-4 space-y-1">
            <li>Las necesidades específicas de tu mascota</li>
            <li>Tu disponibilidad y horarios</li>
            <li>Las recomendaciones de tu veterinario</li>
          </ul>
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default CareTab;